/**
 * Estado do cliente.
 *
 * REGRA (ADR-006): toda a mutação de mensagens vem de EVENTOS do gateway,
 * nunca da resposta REST. O envio só cria uma bolha óptimista marcada
 * `pending`, que o MESSAGE_CREATE correspondente substitui. Há um único
 * caminho de renderização, logo não existe a classe de bugs em que o eco do
 * autor divergiu do que todos os outros viram.
 */
import { create } from 'zustand';
import type { Channel, Guild, Member, Message, PrivateUser, Role } from './rest.js';
import type { GatewayStatus } from './gateway.js';
import type { VoiceQuality, VoiceStats } from '@tasca/shared';
import type { AudioDevice, VoiceConnectionState, RemoteStream } from './voice/client.js';
import { DEFAULT_SETTINGS, type VoiceSettings } from './voice/settings.js';

interface TypingEntry {
  userId: string;
  displayName: string;
  at: number;
}

export interface VoiceMember {
  userId: string;
  channelId: string;
  selfMute: boolean;
  selfDeaf: boolean;
  selfVideo: boolean;
  selfStream: boolean;
  serverMute: boolean;
  serverDeaf: boolean;
}

export interface VoiceServerInfo {
  channelId: string;
  endpoint: string;
  token: string;
  iceServers: { urls: string[]; username?: string; credential?: string }[];
}

interface State {
  me: PrivateUser | null;
  status: GatewayStatus;

  /** userId -> estado de voz. Cobre TODOS os canais do guild, para a
   *  sidebar poder mostrar quem esta em cada canal de voz. */
  voiceStates: Map<string, VoiceMember>;
  voiceState: VoiceConnectionState;
  voiceDetail: string | null;
  voiceChannelId: string | null;
  /** Espelho do estado do VoiceClient — ele é a fonte da verdade. */
  voiceMuted: boolean;
  voiceDeafened: boolean;
  voiceServerMute: boolean;
  voiceServerDeaf: boolean;
  voiceQuality: VoiceQuality;
  voiceStats: VoiceStats | null;
  voiceSettings: VoiceSettings;
  voiceDevices: { inputs: AudioDevice[]; outputs: AudioDevice[]; cameras: AudioDevice[] };
  /**
   * Camada e pontuação por vídeo recebido.
   *
   * Vive aqui e não no componente porque o SFU pode baixar a camada sozinho
   * a qualquer momento: é estado que chega por evento, não resultado de uma
   * acção da UI.
   */
  videoStatus: Map<string, { spatialLayer: number | null; score: number | null }>;
  /** Vídeo em foco, ou `null` para a grelha. Efémero, não é preferência. */
  focusedConsumerId: string | null;
  /** Ultimo VOICE_SERVER_UPDATE: o token tem 60s, logo e consumido logo. */
  pendingVoiceServer: VoiceServerInfo | null;
  remoteStreams: RemoteStream[];
  speaking: Set<string>;

  guilds: Map<string, Guild>;
  /** guildId → canais ordenados */
  channels: Map<string, Channel[]>;
  roles: Map<string, Role[]>;
  members: Map<string, Member[]>;
  /** channelId → mensagens, mais antiga primeiro (ordem de render) */
  messages: Map<string, Message[]>;
  typing: Map<string, TypingEntry[]>;
  readStates: Map<string, string>;
  /**
   * Actividade não vista, por canal, desde que a app está aberta.
   *
   * Não tenta ser "mensagens não lidas de sempre": isso exigiria contagens do
   * servidor para canais cujo histórico nunca foi carregado, e um número
   * errado num badge é pior que não haver badge. Isto conta o que chegou
   * enquanto não estávamos a olhar — que é exactamente o que um badge de
   * tray tem de dizer.
   */
  unread: Map<string, number>;

  activeGuildId: string | null;
  activeChannelId: string | null;

  setMe: (u: PrivateUser | null) => void;
  setStatus: (s: GatewayStatus) => void;
  setVoiceState: (s: VoiceConnectionState, detail?: string) => void;
  setVoiceChannel: (channelId: string | null) => void;
  setVoiceLocalState: (state: {
    muted: boolean;
    deafened: boolean;
    serverMute: boolean;
    serverDeaf: boolean;
  }) => void;
  setVoiceQuality: (quality: VoiceQuality, stats: VoiceStats) => void;
  setVoiceSettings: (settings: VoiceSettings) => void;
  setVoiceDevices: (devices: {
    inputs: AudioDevice[];
    outputs: AudioDevice[];
    cameras: AudioDevice[];
  }) => void;
  setVideoStatus: (
    consumerId: string,
    status: { spatialLayer?: number | null; score?: number },
  ) => void;
  setFocusedConsumer: (consumerId: string | null) => void;
  consumeVoiceServer: () => VoiceServerInfo | null;
  addRemoteStream: (s: RemoteStream) => void;
  removeRemoteStream: (consumerId: string) => void;
  setSpeaking: (peerId: string, speaking: boolean) => void;
  clearVoice: () => void;
  select: (guildId: string | null, channelId: string | null) => void;
  totalUnread: () => number;

  applyEvent: (event: string, data: unknown) => void;
  setHistory: (channelId: string, messages: Message[], prepend?: boolean) => void;
  addOptimistic: (m: Message) => void;
  failOptimistic: (channelId: string, nonce: string) => void;
  reset: () => void;
}

const MAX_PER_CHANNEL = 300;

function upsert(list: Message[], incoming: Message): Message[] {
  // Reconciliação por nonce: substitui a bolha óptimista do autor.
  if (incoming.nonce) {
    const idx = list.findIndex((m) => m.pending && m.nonce === incoming.nonce);
    if (idx >= 0) {
      const next = [...list];
      next[idx] = incoming;
      return next;
    }
  }
  const byId = list.findIndex((m) => m.id === incoming.id);
  if (byId >= 0) {
    const next = [...list];
    next[byId] = { ...next[byId], ...incoming };
    return next;
  }
  const next = [...list, incoming];
  // Snowflakes são ordenáveis por tempo: comparar como string decimal exige
  // mesmo comprimento, logo comparamos por tamanho e depois lexicograficamente.
  next.sort((a, b) =>
    a.id.length === b.id.length ? a.id.localeCompare(b.id) : a.id.length - b.id.length,
  );
  return next.length > MAX_PER_CHANNEL ? next.slice(-MAX_PER_CHANNEL) : next;
}

export const useStore = create<State>((set, get) => ({
  me: null,
  status: 'idle',
  voiceStates: new Map(),
  voiceState: 'idle',
  voiceDetail: null,
  voiceChannelId: null,
  voiceMuted: false,
  voiceDeafened: false,
  voiceServerMute: false,
  voiceServerDeaf: false,
  voiceQuality: 'unknown',
  voiceStats: null,
  // Os valores por omissão, não os guardados: a persistência é da app (o
  // browser tem `localStorage` sincrono, o telemóvel tem armazenamento
  // assíncrono) e ela chama `setVoiceSettings` com o que leu.
  voiceSettings: DEFAULT_SETTINGS,
  voiceDevices: { inputs: [], outputs: [], cameras: [] },
  videoStatus: new Map(),
  focusedConsumerId: null,
  pendingVoiceServer: null,
  remoteStreams: [],
  speaking: new Set(),
  guilds: new Map(),
  channels: new Map(),
  roles: new Map(),
  members: new Map(),
  messages: new Map(),
  typing: new Map(),
  readStates: new Map(),
  unread: new Map(),
  activeGuildId: null,
  activeChannelId: null,

  setMe: (me) => set({ me }),
  setStatus: (status) => set({ status }),

  setVoiceState: (voiceState, voiceDetail) =>
    set({ voiceState, voiceDetail: voiceDetail ?? null }),
  setVoiceChannel: (voiceChannelId) => set({ voiceChannelId }),
  setVoiceLocalState: ({ muted, deafened, serverMute, serverDeaf }) =>
    set({
      voiceMuted: muted,
      voiceDeafened: deafened,
      voiceServerMute: serverMute,
      voiceServerDeaf: serverDeaf,
    }),
  setVoiceQuality: (voiceQuality, voiceStats) => set({ voiceQuality, voiceStats }),
  setVoiceSettings: (voiceSettings) => set({ voiceSettings }),
  setVoiceDevices: (voiceDevices) => set({ voiceDevices }),

  setVideoStatus: (consumerId, status) =>
    set((s) => {
      const videoStatus = new Map(s.videoStatus);
      const previous = videoStatus.get(consumerId) ?? { spatialLayer: null, score: null };
      videoStatus.set(consumerId, {
        spatialLayer: status.spatialLayer !== undefined ? status.spatialLayer : previous.spatialLayer,
        score: status.score !== undefined ? status.score : previous.score,
      });
      return { videoStatus };
    }),

  setFocusedConsumer: (focusedConsumerId) => set({ focusedConsumerId }),

  // O token de voz dura 60s e serve uma vez: ler consome.
  consumeVoiceServer: () => {
    const info = get().pendingVoiceServer;
    if (info) set({ pendingVoiceServer: null });
    return info;
  },

  addRemoteStream: (stream) =>
    set((s) => ({
      remoteStreams: [...s.remoteStreams.filter((r) => r.consumerId !== stream.consumerId), stream],
    })),

  removeRemoteStream: (consumerId) =>
    set((s) => {
      const videoStatus = new Map(s.videoStatus);
      videoStatus.delete(consumerId);
      return {
        remoteStreams: s.remoteStreams.filter((r) => r.consumerId !== consumerId),
        videoStatus,
        // Sem isto, fechar a câmara de quem estava em foco deixava a grelha
        // num modo de foco sobre nada.
        focusedConsumerId: s.focusedConsumerId === consumerId ? null : s.focusedConsumerId,
      };
    }),

  setSpeaking: (peerId, isSpeaking) =>
    set((s) => {
      const speaking = new Set(s.speaking);
      if (isSpeaking) speaking.add(peerId);
      else speaking.delete(peerId);
      return { speaking };
    }),

  clearVoice: () =>
    set({
      voiceState: 'idle',
      voiceDetail: null,
      voiceChannelId: null,
      remoteStreams: [],
      speaking: new Set(),
      pendingVoiceServer: null,
      voiceQuality: 'unknown',
      voiceStats: null,
      videoStatus: new Map(),
      focusedConsumerId: null,
    }),
  select: (activeGuildId, activeChannelId) =>
    set((s) => {
      // Abrir o canal limpa o badge logo, sem esperar pelo ACK: o ACK leva
      // 800ms de propósito (ver Chat.tsx) e um badge que fica aceso depois
      // de abrirmos o canal parece avariado.
      if (!activeChannelId) return { activeGuildId, activeChannelId };
      const unread = new Map(s.unread);
      unread.delete(activeChannelId);
      return { activeGuildId, activeChannelId, unread };
    }),

  /** Soma de tudo o que não foi visto — o número do badge do tray. */
  totalUnread: () => {
    let total = 0;
    for (const count of get().unread.values()) total += count;
    return total;
  },

  setHistory: (channelId, incoming, prepend = false) =>
    set((s) => {
      const existing = s.messages.get(channelId) ?? [];
      // O servidor devolve mais-recente-primeiro; a UI renderiza ao contrário.
      const ordered = [...incoming].reverse();
      const merged = prepend ? [...ordered, ...existing] : ordered;
      const dedup = new Map(merged.map((m) => [m.id, m]));
      const messages = new Map(s.messages);
      messages.set(channelId, [...dedup.values()]);
      return { messages };
    }),

  addOptimistic: (m) =>
    set((s) => {
      const messages = new Map(s.messages);
      messages.set(m.channelId, [...(s.messages.get(m.channelId) ?? []), m]);
      return { messages };
    }),

  failOptimistic: (channelId, nonce) =>
    set((s) => {
      const list = s.messages.get(channelId);
      if (!list) return {};
      const messages = new Map(s.messages);
      messages.set(
        channelId,
        list.map((m) => (m.nonce === nonce && m.pending ? { ...m, failed: true } : m)),
      );
      return { messages };
    }),

  reset: () =>
    set({
      me: null,
      guilds: new Map(),
      channels: new Map(),
      roles: new Map(),
      members: new Map(),
      messages: new Map(),
      typing: new Map(),
      readStates: new Map(),
      unread: new Map(),
      activeGuildId: null,
      activeChannelId: null,
    }),

  applyEvent: (event, raw) => {
    const s = get();

    switch (event) {
      case 'READY': {
        const d = raw as {
          user: PrivateUser;
          readStates: { channelId: string; lastReadId: string }[];
        };
        set({
          me: d.user,
          readStates: new Map(d.readStates.map((r) => [r.channelId, r.lastReadId])),
        });
        return;
      }

      case 'GUILD_CREATE': {
        const g = raw as Guild;
        const guilds = new Map(s.guilds);
        guilds.set(g.id, g);
        const channels = new Map(s.channels);
        channels.set(g.id, [...(g.channels ?? [])].sort((a, b) => a.position - b.position));
        const roles = new Map(s.roles);
        roles.set(g.id, [...(g.roles ?? [])].sort((a, b) => b.position - a.position));

        const patch: Partial<State> = { guilds, channels, roles };
        // Primeiro guild a chegar: selecciona-o para a UI não ficar vazia.
        if (!s.activeGuildId) {
          const first = (g.channels ?? []).find((c) => c.type === 0);
          patch.activeGuildId = g.id;
          patch.activeChannelId = first?.id ?? null;
        }
        set(patch);
        return;
      }

      case 'GUILD_UPDATE': {
        const g = raw as Guild;
        const guilds = new Map(s.guilds);
        const prev = guilds.get(g.id);
        guilds.set(g.id, { ...prev, ...g });
        set({ guilds });
        return;
      }

      case 'GUILD_DELETE': {
        const d = raw as { id: string };
        const guilds = new Map(s.guilds);
        guilds.delete(d.id);
        const channels = new Map(s.channels);
        channels.delete(d.id);
        const patch: Partial<State> = { guilds, channels };
        if (s.activeGuildId === d.id) {
          const next = [...guilds.values()][0];
          patch.activeGuildId = next?.id ?? null;
          patch.activeChannelId = null;
        }
        set(patch);
        return;
      }

      case 'CHANNEL_CREATE':
      case 'CHANNEL_UPDATE': {
        const c = raw as Channel;
        if (!c.guildId) return;
        const channels = new Map(s.channels);
        const list = [...(channels.get(c.guildId) ?? [])];
        const idx = list.findIndex((x) => x.id === c.id);
        if (idx >= 0) list[idx] = { ...list[idx], ...c };
        else list.push(c);
        channels.set(c.guildId, list.sort((a, b) => a.position - b.position));
        set({ channels });
        return;
      }

      case 'CHANNEL_DELETE': {
        const d = raw as { id: string; guildId: string };
        const channels = new Map(s.channels);
        const list = (channels.get(d.guildId) ?? []).filter((c) => c.id !== d.id);
        channels.set(d.guildId, list);
        const patch: Partial<State> = { channels };
        // Um canal a que perdeste acesso não deve ficar na sidebar, morto.
        if (s.activeChannelId === d.id) {
          patch.activeChannelId = list.find((c) => c.type === 0)?.id ?? null;
        }
        set(patch);
        return;
      }

      case 'GUILD_ROLE_CREATE':
      case 'GUILD_ROLE_UPDATE': {
        const r = raw as Role;
        const roles = new Map(s.roles);
        const list = [...(roles.get(r.guildId) ?? [])];
        const idx = list.findIndex((x) => x.id === r.id);
        if (idx >= 0) list[idx] = r;
        else list.push(r);
        roles.set(r.guildId, list.sort((a, b) => b.position - a.position));
        set({ roles });
        return;
      }

      case 'GUILD_ROLE_DELETE': {
        const d = raw as { id: string; guildId: string };
        const roles = new Map(s.roles);
        roles.set(d.guildId, (roles.get(d.guildId) ?? []).filter((r) => r.id !== d.id));
        set({ roles });
        return;
      }

      case 'GUILD_MEMBER_ADD':
      case 'GUILD_MEMBER_UPDATE': {
        const m = raw as Member;
        const members = new Map(s.members);
        const list = [...(members.get(m.guildId) ?? [])];
        const idx = list.findIndex((x) => x.userId === m.userId);
        if (idx >= 0) list[idx] = m;
        else list.push(m);
        members.set(m.guildId, list);
        return set({ members });
      }

      case 'GUILD_MEMBER_REMOVE': {
        const d = raw as { guildId: string; userId: string };
        const members = new Map(s.members);
        members.set(
          d.guildId,
          (members.get(d.guildId) ?? []).filter((m) => m.userId !== d.userId),
        );
        set({ members });
        return;
      }

      case 'GUILD_MEMBERS_CHUNK': {
        const d = raw as { guildId: string; members: Member[] };
        const members = new Map(s.members);
        const existing = members.get(d.guildId) ?? [];
        const merged = new Map(existing.map((m) => [m.userId, m]));
        for (const m of d.members) merged.set(m.userId, m);
        members.set(d.guildId, [...merged.values()]);
        set({ members });
        return;
      }

      case 'MESSAGE_CREATE': {
        const m = raw as Message;
        const messages = new Map(s.messages);
        messages.set(m.channelId, upsert(s.messages.get(m.channelId) ?? [], m));

        // Quem acabou de escrever já não está a escrever.
        const typing = new Map(s.typing);
        typing.set(
          m.channelId,
          (typing.get(m.channelId) ?? []).filter((t) => t.userId !== m.author?.id),
        );

        const channels = new Map(s.channels);
        if (m.guildId) {
          const list = (channels.get(m.guildId) ?? []).map((c) =>
            c.id === m.channelId ? { ...c, lastMessageId: m.id } : c,
          );
          channels.set(m.guildId, list);
        }

        // Conta como não vista se não for nossa e não estivermos a olhar
        // para esse canal. `document.hidden` entra na conta porque o canal
        // activo com a janela minimizada também não está a ser lido — é o
        // caso em que o badge do tray importa.
        const unread = new Map(s.unread);
        const looking =
          m.channelId === s.activeChannelId &&
          (typeof document === 'undefined' || !document.hidden);
        if (m.author?.id !== s.me?.id && !looking && !m.pending) {
          unread.set(m.channelId, (unread.get(m.channelId) ?? 0) + 1);
        }

        set({ messages, typing, channels, unread });
        return;
      }

      case 'MESSAGE_UPDATE': {
        const m = raw as Message;
        const messages = new Map(s.messages);
        messages.set(m.channelId, upsert(s.messages.get(m.channelId) ?? [], m));
        set({ messages });
        return;
      }

      case 'MESSAGE_DELETE': {
        const d = raw as { id: string; channelId: string };
        const messages = new Map(s.messages);
        messages.set(
          d.channelId,
          (messages.get(d.channelId) ?? []).filter((m) => m.id !== d.id),
        );
        set({ messages });
        return;
      }

      case 'MESSAGE_REACTION_ADD':
      case 'MESSAGE_REACTION_REMOVE': {
        const d = raw as {
          messageId: string;
          channelId: string;
          userId: string;
          emoji: string;
        };
        const add = event === 'MESSAGE_REACTION_ADD';
        const mine = d.userId === s.me?.id;
        const messages = new Map(s.messages);
        messages.set(
          d.channelId,
          (messages.get(d.channelId) ?? []).map((m) => {
            if (m.id !== d.messageId) return m;
            const reactions = [...m.reactions];
            const idx = reactions.findIndex((r) => r.emoji === d.emoji);
            if (idx >= 0) {
              const r = reactions[idx]!;
              const count = r.count + (add ? 1 : -1);
              if (count <= 0) reactions.splice(idx, 1);
              else reactions[idx] = { ...r, count, me: mine ? add : r.me };
            } else if (add) {
              reactions.push({ emoji: d.emoji, count: 1, me: mine });
            }
            return { ...m, reactions };
          }),
        );
        set({ messages });
        return;
      }

      case 'TYPING_START': {
        const d = raw as {
          channelId: string;
          userId: string;
          member: { user: { displayName: string } };
        };
        if (d.userId === s.me?.id) return;
        const typing = new Map(s.typing);
        const list = (typing.get(d.channelId) ?? []).filter((t) => t.userId !== d.userId);
        list.push({
          userId: d.userId,
          displayName: d.member.user.displayName,
          at: Date.now(),
        });
        typing.set(d.channelId, list);
        set({ typing });
        return;
      }

      case 'VOICE_SERVER_UPDATE': {
        const d = raw as VoiceServerInfo;
        set({ pendingVoiceServer: d });
        return;
      }

      case 'VOICE_STATE_UPDATE': {
        const d = raw as VoiceMember & { channelId: string | null };
        const voiceStates = new Map(s.voiceStates);
        const speaking = new Set(s.speaking);
        if (d.channelId === null) {
          voiceStates.delete(d.userId);
          // Sair a falar deixava o anel verde aceso para sempre: ninguém
          // mais vai enviar um SPEAKING_UPDATE por esta pessoa.
          speaking.delete(d.userId);
        } else {
          voiceStates.set(d.userId, d as VoiceMember);
        }
        set({ voiceStates, speaking });
        return;
      }

      case 'VOICE_CHANNEL_STATE': {
        const d = raw as { channelId: string; states: VoiceMember[] };
        const voiceStates = new Map(s.voiceStates);
        for (const st of d.states) {
          if (st.channelId) voiceStates.set(st.userId, st);
        }
        set({ voiceStates });
        return;
      }

      case 'SPEAKING_UPDATE': {
        const d = raw as { userId: string; speaking: boolean };
        // O próprio vem do VAD local (instantâneo, sem ida ao servidor); do
        // gateway só interessam os outros, senão o anel pisca duas vezes.
        if (d.userId === s.me?.id) return;
        const speaking = new Set(s.speaking);
        if (d.speaking) speaking.add(d.userId);
        else speaking.delete(d.userId);
        set({ speaking });
        return;
      }

      case 'VOICE_CHANNEL_FULL': {
        set({ voiceState: 'failed', voiceDetail: 'o canal de voz está cheio' });
        return;
      }

      case 'MESSAGE_ACK': {
        const d = raw as { channelId: string; lastReadId: string };
        const readStates = new Map(s.readStates);
        readStates.set(d.channelId, d.lastReadId);
        // Ler apaga a contagem: o ACK é a confirmação do servidor de que
        // chegámos ao fim da conversa.
        const unread = new Map(s.unread);
        unread.delete(d.channelId);
        set({ readStates, unread });
        return;
      }

      default:
        return;
    }
  },
}));

/** Indicadores de escrita expiram a 8s — o servidor não envia "parou". */
export function activeTyping(entries: TypingEntry[] | undefined): TypingEntry[] {
  if (!entries) return [];
  const cutoff = Date.now() - 8000;
  return entries.filter((t) => t.at > cutoff);
}
